import {
    Box, Button, Flex, FormControl, FormLabel, Input, VStack,
    Heading, Text, FormErrorMessage
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
    Cookie,
    AutoSave
} from '../../../events/pages/AllEventsPage/cookie-utils';
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";

const TicketDataStep = ({ data, update, next, prev, t }) => {
    const isFree = !!data?.isFree;
    const cart = data?.cart ?? {};
    const ticketTypes = data?.ticketTypes ?? [];
    const eventId = data?.eventId ?? "global";
    const COOKIE_KEY = `checkout:${eventId}:attendees`;

    // ======== WYZNACZENIE LISTY EGZEMPLARZY BILETÓW ========

    let totalTickets; // tablica indeksów / ID do wyświetlenia pól
    let nameResolver; // funkcja zwracająca nazwę biletu dla nagłówka

    if (isFree) {
        // mamy tylko liczbę darmowych biletów
        const qty = Math.max(0, Number(cart.quantity ?? 0));
        totalTickets = Array.from({ length: qty }, () => null);
        nameResolver = () => t?.("ticket.standard") || "Bilet standardowy";
    } else {
        // płatne: rozkładamy koszyk po ilościach
        const personalizedIds = new Set(
            ticketTypes.filter(tt => tt.isPersonalized).map(tt => String(tt.id))
        );

        const allSelected = Object.entries(cart?.quantities || {})
            .flatMap(([ticketId, qty]) => Array(qty).fill(String(ticketId)));

        // pokazujemy tylko bilety, które wymagają personalizacji
        totalTickets = allSelected.filter(id => personalizedIds.has(String(id)));

        nameResolver = (ticketId) => {
            const tt = ticketTypes.find(x => String(x.id) === String(ticketId));
            return tt?.name ?? (t?.("ticket.standard") || "Bilet standardowy");
        };
    }

    // Jeżeli nie ma co personalizować (płatne) – pomiń krok
    if (!isFree && totalTickets.length === 0) {
        return (
            <VStack spacing={6} align="stretch">
                <Text fontFamily="body" fontSize="18px" fontWeight="500">
                    {t("order.step.tickets_data.title")}
                </Text>
                <Box bg="#F8F5F4" p={4} borderRadius="8px">
                    <Text>
                        {t("order.step.tickets_data.no_personalization") ||
                            "Wybrane bilety nie wymagają podawania danych uczestników."}
                    </Text>
                </Box>
                <Box display="flex" justifyContent="space-between" gap="16px">
                    <Button
                        w="20%"
                        bgColor="#FFFFFF"
                        color="#7A1F35"
                        fontFamily="body"
                        fontWeight="400"
                        fontSize="18px"
                        py="30px"
                        gap="12px"
                        style={{ border: "1.5px solid #7A1F35" }}
                        onClick={prev}
                    >
                        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 20L3 12.5M3 12.5L10.5 5M3 12.5L21 12.5" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Text display={{ base: 'none', md: 'block' }}>
                            {t("order.form.come_back")}
                        </Text>
                    </Button>
                    <Button
                        w="full"
                        bgColor="#7A1F35"
                        color="#FFFFFF"
                        fontFamily="body"
                        fontWeight="400"
                        fontSize="18px"
                        py="30px"
                        gap="12px"
                        onClick={next}
                    >
                        {t("order.form.to_summary")}
                        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 5L21.5 12.5M21.5 12.5L14 20M21.5 12.5H3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Button>
                </Box>
            </VStack>
        );
    }

    // ======== INITIAL VALUES & VALIDACJA ========

    // ===== Prefill z cookie / data =====
    const cookieAtt = Cookie.get(COOKIE_KEY)?.attendees ?? [];
    const getExisting = (idx) => cookieAtt[idx] ?? data.attendees?.[idx];

    const initialValues = totalTickets.reduce((acc, _ticketId, idx) => {
        const existing = getExisting(idx);
        const prefillSingle = totalTickets.length === 1;
        acc[`firstName_${idx}`] = existing?.firstName || (prefillSingle ? data.firstName || "" : "");
        acc[`lastName_${idx}`] = existing?.lastName || (prefillSingle ? data.lastName || "" : "");
        acc[`email_${idx}`] = existing?.email || (prefillSingle ? data.email || "" : "");
        acc[`phone_${idx}`] = existing?.phone || (prefillSingle ? data.phone || "" : "");
        return acc;
    }, {});

    const validationSchema = Yup.object(
        totalTickets.reduce((acc, _, idx) => {
            acc[`firstName_${idx}`] = Yup.string().required(t("order.form.validation.firstName") || "Imię jest wymagane");
            acc[`lastName_${idx}`] = Yup.string().required(t("order.form.validation.lastName") || "Nazwisko jest wymagane");
            acc[`email_${idx}`] = Yup.string()
                .email(t("order.form.validation.email_invalid") || "Nieprawidłowy e-mail")
                .required(t("order.form.validation.email") || "E-mail jest wymagany");
            acc[`phone_${idx}`] = Yup.string();
            return acc;
        }, {})
    );

    // Helper: z wartości formularza budujemy tablicę attendees
    const buildAttendees = (values) =>
        totalTickets.map((ticketId, index) => ({
            ...(isFree ? {} : { eventTicketTypeId: ticketId }),
            firstName: values[`firstName_${index}`],
            lastName: values[`lastName_${index}`],
            email: values[`email_${index}`],
            phone: values[`phone_${index}`],
        }));

    const handleSave = (vals) => {
        const attendees = buildAttendees(vals);
        Cookie.set(COOKIE_KEY, { attendees }, 7);
        update({ attendees });
    };

    // ======== RENDER ========

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={(values) => {
                handleSave(values);
                next();
            }}
        >
            {({ isSubmitting, errors, touched, values }) => (
                <Form>
                    {/* auto-zapis do cookie przy każdej zmianie */}
                    <AutoSave values={values} onSave={handleSave} />
                    <VStack spacing={6} align="stretch">
                        <Text fontFamily="body" fontSize="18px" fontWeight="500">
                            {t("order.step.tickets_data.title")}
                        </Text>

                        {totalTickets.map((ticketId, index) => (
                            <Box key={`${ticketId ?? "FREE"}-${index}`} borderRadius="8px" bgColor="#F8F5F4" p={4} gap="8px">
                                <Heading size="sm" mb={4} fontWeight="700">
                                    <Text as="span" color="#7A1F35">#{index + 1}</Text>{" "}
                                    <Text as="span">
                                        {isFree ? (t?.("ticket.standard") || "Bilet standardowy")
                                            : nameResolver(ticketId)}
                                    </Text>
                                </Heading>

                                <Flex gap="16px" alignItems="flex-end">
                                    <Box flex="1" alignSelf="start" p={1}>
                                        <InlineErrorInput
                                            name={`firstName_${index}`}
                                            label={t("order.form.name")}
                                            isRequired
                                            bgColor="#FFFFFF"
                                            autoComplete="given-name"
                                        />
                                    </Box>
                                    <Box flex="1" alignSelf="start" p={1}>
                                        <InlineErrorInput
                                            name={`lastName_${index}`}
                                            label={t("order.form.lastName")}
                                            isRequired
                                            bgColor="#FFFFFF"
                                            autoComplete="family-name"
                                        />
                                    </Box>
                                </Flex>

                                <Flex gap="16px" alignItems="flex-end">
                                    <Box flex="1" alignSelf="start" p={1}>
                                        <InlineErrorInput
                                            name={`email_${index}`}
                                            type="email"
                                            label={t("order.form.email")}
                                            isRequired
                                            bgColor="#FFFFFF"
                                            autoComplete="email"
                                        />
                                    </Box>
                                    <Box flex="1" alignSelf="start" p={1}>
                                        <InlineErrorInput
                                            name={`phone_${index}`}
                                            type="tel"
                                            label={t("order.form.phone")}
                                            bgColor="#FFFFFF"
                                            autoComplete="tel"
                                        />
                                    </Box>
                                </Flex>
                            </Box>
                        ))}

                        <Box display="flex" justifyContent="space-between" gap={{ base: '8px', md: '16px' }}>
                            <Button
                                w={{ base: '35%', md: '20%' }}
                                bgColor="#FFFFFF"
                                color="#7A1F35"
                                fontFamily="body"
                                fontWeight="400"
                                fontSize={{ base: '14px', md: '18px' }}
                                py="30px"
                                gap="12px"
                                style={{ border: "1.5px solid #7A1F35" }}
                                onClick={prev}
                            >
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.5 20L3 12.5M3 12.5L10.5 5M3 12.5L21 12.5" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <Text display={{ base: 'none', md: 'block' }}>
                                    {t("order.form.come_back")}
                                </Text>
                            </Button>

                            <Button
                                w="full"
                                bgColor="#7A1F35"
                                color="#FFFFFF"
                                fontFamily="body"
                                fontWeight="400"
                                fontSize={{ base: '14px', md: '18px' }}
                                py="30px"
                                gap="12px"
                                type="submit"
                                isLoading={isSubmitting}
                            >
                                {t("order.form.to_summary")}
                                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 5L21.5 12.5M21.5 12.5L14 20M21.5 12.5H3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Button>
                        </Box>
                    </VStack>
                </Form>
            )}
        </Formik>
    );
};

export default TicketDataStep;
